/**
 * Wishlist / favorites: toggles customer metafields via the app proxy.
 * Expects window.themeSettings.wishlistAppBase ({shop.url}/apps/viva-sara) and routes.account_login_url (set from Liquid).
 */

/**
 * @typedef {{ wishlistAppBase?: string, routes?: { account_login_url?: string } }} ThemeSettings
 */

/**
 * @returns {ThemeSettings | undefined}
 */
function getThemeSettings() {
  return /** @type {Window & { themeSettings?: ThemeSettings }} */ (window).themeSettings;
}

const METAFIELD_NAMESPACE = 'custom';

/**
 * @returns {string} Same-origin app proxy base (shop URL + /apps/viva-sara)
 */
function getWishlistAppBase() {
  const base = getThemeSettings()?.wishlistAppBase?.trim?.() ?? '';
  return base ? base.replace(/\/$/, '') : '';
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} loading
 */
function setLoading(button, loading) {
  button.toggleAttribute('aria-busy', loading);
  button.disabled = loading;
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} favorited
 */
function applyFavoriteState(button, favorited) {
  button.dataset.isFavorited = favorited ? 'true' : 'false';
  button.setAttribute('aria-pressed', favorited ? 'true' : 'false');
  button.classList.toggle('wishlist-button--active', favorited);
}

/**
 * @param {HTMLButtonElement} button
 */
function removeFavoritesPageRow(button) {
  const row = button.closest('.resource-list__item');
  if (row?.parentElement) {
    row.remove();
  } else {
    window.location.reload();
    return;
  }
  showWishlistEmptyStateIfNoItems(button);
}

/**
 * When the last favorite is removed on the wishlist page, show the same empty message as SSR (no full reload).
 * @param {HTMLButtonElement} button
 */
function showWishlistEmptyStateIfNoItems(button) {
  const section = button.closest('[data-wishlist-favorites-section]');
  if (!section) return;
  const gridRegion = section.querySelector('[data-wishlist-favorites-grid-region]');
  const emptyEl = section.querySelector('[data-wishlist-favorites-empty]');
  if (!gridRegion || !emptyEl) return;
  const items = section.querySelectorAll('.resource-list__item');
  if (items.length > 0) return;
  /** @type {HTMLElement} */ (gridRegion).hidden = true;
  /** @type {HTMLElement} */ (emptyEl).hidden = false;
}

/**
 * @param {number} delta
 */
function updateWishlistHeaderCount(delta) {
  const bubbles = document.querySelectorAll('[data-wishlist-count]');
  bubbles.forEach((bubble) => {
    let count = parseInt(bubble.dataset.wishlistCount || '0', 10);
    count = Math.max(0, count + delta);
    bubble.dataset.wishlistCount = String(count);
    const countEl = bubble.querySelector('.wishlist-bubble__count');
    if (countEl) countEl.textContent = String(count);
    bubble.classList.toggle('visually-hidden', count === 0);
    const icon = bubble.closest('.wishlist-icon');
    if (icon) icon.classList.toggle('wishlist-icon--has-items', count > 0);
  });
}

document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest('button[data-wishlist-trigger]');
  if (!(el instanceof HTMLButtonElement)) return;
  const button = el;

  event.preventDefault();

  const appBase = getWishlistAppBase();
  if (!appBase) {
    console.warn('[wishlist] Missing themeSettings.wishlistAppBase (shop app proxy URL).');
    return;
  }

  const customerId = button.dataset.customerId;
  if (!customerId) {
    const tooltipId = button.dataset.wishlistLoginTooltip;
    if (tooltipId) {
      const tooltip = /** @type {HTMLElement | null} */ (document.getElementById(tooltipId));
      if (tooltip && typeof tooltip.showPopover === 'function') {
        // Move to body to escape any transformed ancestor (which breaks position:fixed)
        if (tooltip.parentElement !== document.body) {
          document.body.appendChild(tooltip);
        }
        tooltip.showPopover();
        const rect = button.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        tooltip.style.top = `${rect.top - tooltipRect.height - 8}px`;
        tooltip.style.left = `${rect.left + rect.width / 2 - tooltipRect.width / 2}px`;
        tooltip.style.transform = 'none';

        const autoClose = setTimeout(() => {
          if (tooltip.matches(':popover-open')) tooltip.hidePopover();
        }, 3000);

        const onScroll = () => {
          if (tooltip.matches(':popover-open')) tooltip.hidePopover();
          clearTimeout(autoClose);
          window.removeEventListener('scroll', onScroll, true);
        };
        window.addEventListener('scroll', onScroll, true);
      }
    }
    return;
  }

  const productId = button.dataset.productId;
  const metafieldKey = button.dataset.metafieldKey || 'wishlist_products';
  if (!productId) {
    console.error('[wishlist] Missing data-product-id on button');
    return;
  }

  const wasFavorited = button.dataset.isFavorited === 'true';
  const method = wasFavorited ? 'DELETE' : 'POST';
  const url = `${appBase}/customers/${customerId}/wishlist`;

  const body = JSON.stringify({
    metafield_namespace: METAFIELD_NAMESPACE,
    metafield_key: metafieldKey,
    metafield_value: productId,
  });

  setLoading(button, true);

  try {
    const response = await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const nowFavorited = !wasFavorited;
    applyFavoriteState(button, nowFavorited);
    updateWishlistHeaderCount(nowFavorited ? 1 : -1);

    if (button.dataset.wishlistContext === 'favorites-page' && !nowFavorited) {
      removeFavoritesPageRow(button);
    }
  } catch (error) {
    console.error('[wishlist] API error:', error);
  } finally {
    setLoading(button, false);
  }
});
